export type FunkiMessageRole = 'assistant' | 'user'

// Location types for nearby search
export interface FunkiLocation {
  lat: number
  lon: number
}

export interface FunkiMessageCreationPayload {
  content: string
  lat?: number
  lon?: number
}

export interface FunkiSearchQuery {
  start_datetime: string | null
  end_datetime: string | null
  party_size: number | null
  location: string[] | null
  criteria: string[] | null
  budget: number | null
}

export interface FunkiMapContext {
  region_id: number
  branch_ids: number[]
}

export interface FunkiSearch {
  card_type: 'large_info' | 'large_date' | 'large_time' | 'mini'
  query: FunkiSearchQuery
  map_context?: FunkiMapContext
}

export interface FunkiMessage {
  id: string
  role: FunkiMessageRole
  content: string
  quick_replies: string[]
  recommendations: FunkiRecommendation[]
  search: FunkiSearch | null
  created_at: number
}

export interface FunkiUIMessage extends Omit<FunkiMessage, 'quick_replies'> {
  quickReplies?: string[]
  isMocked?: boolean
  isNew?: boolean
  /** True between first SSE event and thread.message.completed */
  isStreaming?: boolean
  /** Currently displayed progress bubble text (i18n or BE message) */
  progressText?: string
  /** Accumulated delta content fed into typewriter */
  streamingBuffer?: string
  /** BE-assigned message id, set on thread.message.completed (temp id never changes) */
  serverId?: string
}

export interface FunkiRecommendation {
  branch_name: string
  product_name: string
  description: string
  link: string
  image: string
  review_count: number
  review_avg_rating: number
  symbol_code: string
  discount: number
  discount_ratio: number
  discount_info: FunkiDiscountInfo | null
  is_limited: boolean
  is_hot: boolean
  branch_id: number
  product_id: number
  booking_times: string[]
}

export type FunkiCardType = 'large_info' | 'large_date' | 'large_time' | 'mini'
export interface FunkiProductCard {
  branch_id: number
  product_id: number
  branch_name: string
  product_name: string
  link: string
  image: string
  review_avg_rating: number
  review_count: number
  avg_price?: string // 方案價格 - 目前用作 displayPrice 顯示
  description: string
  badge?: FunkiBadgeInfo
  symbol_code?: string
  discount?: number // 優惠折扣金額 - 暫時註解，未來用於均消顯示
  discount_ratio?: number
  discount_info?: FunkiDiscountInfo | null
  is_limited?: boolean
  is_hot?: boolean
  is_new_product?: boolean
  booking_times?: string[]
  member_reward?: string
  vip_reward?: string
  // TODO: 未來新增以下欄位用於更精確的價格顯示
  // package_price?: string // 專門的方案價格欄位
  // avg_consumption?: string // 店家均消欄位
}

export interface FunkiActionOption {
  label: string
  link: string
}

export interface FunkiPriceInfo {
  currency: string
  amount: number
  suffix?: string
}

export interface FunkiBadgeInfo {
  text: string
}

export type FunkiFeedbackVote = 'up' | 'down'

export interface FunkiFeedbackCreationPayload {
  message_id: string
  vote: FunkiFeedbackVote
  reason?: string
}

export interface FunkiDiscountInfo {
  type: number
  time_type: string
  time: string
  rate: number
}

// API Response Types
export interface FunkiTokenResponse {
  code: number
  data: {
    access_token: string
    expires_in: number
    token_type: string
  }
  message: string
}

export interface FunkiThreadResponse {
  data: {
    id: string
  }
}

export interface FunkiMessageResponse {
  data: FunkiMessage
}

export interface FunkiMessagesResponse {
  data: FunkiMessage[]
  pagination: {
    next_page_token: string
  }
}

// Request options type for FunkiApiClient
export interface FunkiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  headers?: Record<string, string>
  body?: any
  query?: Record<string, any>
}

// Error types
export interface FunkiApiError extends Error {
  status?: number
  isTokenExpired?: boolean
}

export enum FunkiSSEEventType {
  PROGRESS = 'thread.progress',
  DELTA = 'thread.message.delta',
  COMPLETED = 'thread.message.completed',
  ERROR = 'error',
  DONE = 'done',
}

// SSE Event Types — discriminated union for Funki streaming
export type FunkiSSEEvent =
  | { type: FunkiSSEEventType.PROGRESS; data: { message?: string } }
  | { type: FunkiSSEEventType.DELTA; data: { content: string } }
  | { type: FunkiSSEEventType.COMPLETED; data: FunkiMessageCompletedPayload }
  | { type: FunkiSSEEventType.ERROR; data: { reason?: 'malformed' | 'transport' | 'server'; message?: string; code?: string; raw?: string } }
  | { type: FunkiSSEEventType.DONE }

export interface FunkiMessageCompletedPayload {
  id: string
  content: string
  role: 'assistant'
  search?: FunkiSearch | null
  recommendations?: FunkiRecommendation[]
  quick_replies?: string[]
  map_context?: unknown
  [k: string]: unknown
}

// Query Chip types — AI-183 (Funki search condition chips)
export type QueryChipType = 'date' | 'time' | 'partySize' | 'location' | 'criteria' | 'budget'

export interface QueryChip {
  id: string
  type: QueryChipType
  field: keyof FunkiSearchQuery
  displayText: string
  rawValue: string | number
  index?: number // used to distinguish multiple location[]/criteria[] chips of same type
}
